import { type NodeChange, type EdgeChange } from '@xyflow/react';
import { useState } from 'react';
import { createStore, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';

import { uuidv4 } from 'helpers/uuid';

import { NODE_GAP_X } from './constants';
import {
  ActionType,
  NodeStatus,
  PaymentRoutingPolicy,
  PickerMode,
  type PickerState,
  type RoutingPath,
  TriggerType,
  type WorkflowEdge,
  type WorkflowNode,
  WorkflowNodeType,
} from './types';

export type InitialGraph = { nodes: WorkflowNode[]; edges: WorkflowEdge[]; name?: string };

type GraphState = {
  nodesById: Record<string, WorkflowNode>;
  edgesById: Record<string, WorkflowEdge>;
};

const PATH_HANDLE_PREFIX = 'path-';

// Removing a ranked payout node (or its edge) must also drop the routing node's
// matching path, or the staging node keeps a phantom ranked row + dead handle.
function detachNode(state: GraphState, nodeId: string) {
  for (const edge of Object.values(state.edgesById)) {
    if (edge.source !== nodeId && edge.target !== nodeId) {
      continue;
    }
    if (
      edge.target === nodeId &&
      typeof edge.sourceHandle === 'string' &&
      edge.sourceHandle.startsWith(PATH_HANDLE_PREFIX)
    ) {
      const routingNode = state.nodesById[edge.source];
      if (routingNode != null && routingNode.data.nodeType === WorkflowNodeType.Routing) {
        const pathId = edge.sourceHandle.slice(PATH_HANDLE_PREFIX.length);
        const routingData = routingNode.data as { paths: RoutingPath[] };
        routingData.paths = routingData.paths.filter((p) => p.id !== pathId);
      }
    }
    delete state.edgesById[edge.id];
  }
  delete state.nodesById[nodeId];
}

// Keep a routing node's ranked path label/amount in lockstep with the payout
// node it points at, so editing the payout config updates the staging list.
function syncRoutingPathFromPayout(state: GraphState, payoutNodeId: string) {
  const payout = state.nodesById[payoutNodeId];
  if (payout == null || payout.data.nodeType !== WorkflowNodeType.SupplierPayout) {
    return;
  }
  const payoutData = payout.data;
  for (const edge of Object.values(state.edgesById)) {
    if (
      edge.target !== payoutNodeId ||
      typeof edge.sourceHandle !== 'string' ||
      !edge.sourceHandle.startsWith(PATH_HANDLE_PREFIX)
    ) {
      continue;
    }
    const routingNode = state.nodesById[edge.source];
    if (routingNode == null || routingNode.data.nodeType !== WorkflowNodeType.Routing) {
      continue;
    }
    const pathId = edge.sourceHandle.slice(PATH_HANDLE_PREFIX.length);
    const routingData = routingNode.data as { paths: RoutingPath[] };
    const path = routingData.paths.find((p) => p.id === pathId);
    if (path != null) {
      path.label = payoutData.label;
      path.amountUsdc = payoutData.amountUsdc;
      path.supplierId = payoutData.supplierId;
    }
  }
}

type WorkflowMeta = {
  name: string;
  isDirty: boolean;
};

type WorkflowState = {
  meta: WorkflowMeta;
  nodesById: Record<string, WorkflowNode>;
  edgesById: Record<string, WorkflowEdge>;
  pickerState: PickerState;
  configNodeId: string | null;
};

type WorkflowActions = {
  setName: (name: string) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  addTriggerNode: (triggerType: TriggerType, position?: { x: number; y: number }) => string;
  addActionNode: (actionType: ActionType, position?: { x: number; y: number }) => string;
  addRoutingNode: (position?: { x: number; y: number }) => string;
  addPaymentLinkNode: (position?: { x: number; y: number }) => string;
  addSupplierPayoutNode: (position?: { x: number; y: number }) => string;
  setRoutingPolicy: (nodeId: string, policy: PaymentRoutingPolicy) => void;
  addSupplierBranch: (routingNodeId: string) => void;
  removeNode: (nodeId: string) => void;
  updateNodeData: (nodeId: string, data: Partial<WorkflowNode['data']>) => void;
  addEdge: (sourceId: string, targetId: string, sourceHandle?: string) => void;
  removeEdge: (edgeId: string) => void;
  openPicker: (state: Omit<PickerState, 'open'>) => void;
  closePicker: () => void;
  openConfig: (nodeId: string) => void;
  closeConfig: () => void;
  replaceNode: (nodeId: string, catalogItemId: string, nodeType: WorkflowNodeType, triggerType?: TriggerType, actionType?: ActionType) => void;
  insertNodeOnEdge: (edgeId: string, nodeId: string) => void;
  addRoutingPath: (nodeId: string, path: RoutingPath) => void;
  removeRoutingPath: (nodeId: string, pathId: string) => void;
  discard: () => void;
};

type WorkflowStore = WorkflowState & { actions: WorkflowActions };

const TRIGGER_LABELS: Record<TriggerType, { label: string; description: string }> = {
  [TriggerType.DemandCreated]: { label: 'Demand Created', description: 'Triggers on new business demand' },
  [TriggerType.BidReceived]: { label: 'Bid Received', description: 'Triggers on supplier bid received' },
  [TriggerType.OrderFulfilled]: { label: 'Order Fulfilled', description: 'Triggers on order fulfillment' },
  [TriggerType.RiskAlertRaised]: { label: 'Risk Alert Raised', description: 'Triggers on risk alert detected' },
  [TriggerType.CommitmentStatusChanged]: { label: 'Commitment Status Changed', description: 'Triggers when a commitment changes status' },
  [TriggerType.DeliveryDeadlineApproaching]: { label: 'Delivery Deadline Approaching', description: 'Triggers before a delivery deadline' },
  [TriggerType.CommitmentWithdrawn]: { label: 'Commitment Withdrawn', description: 'Triggers when a commitment is withdrawn' },
};

const ACTION_LABELS: Record<ActionType, { label: string; description: string }> = {
  [ActionType.SendNotification]: { label: 'Send Notification', description: 'Notify stakeholders' },
  [ActionType.EvaluateRisk]: { label: 'Evaluate Risk', description: 'Run risk assessment' },
  [ActionType.UpdateCompliance]: { label: 'Update Compliance', description: 'Update compliance records' },
  [ActionType.EscalateToReview]: { label: 'Escalate to Review', description: 'Escalate for review decision' },
  [ActionType.CreateNeed]: { label: 'Create Need', description: 'Auto-create a need for a supply gap' },
  [ActionType.NotifyPartner]: { label: 'Notify Partner', description: 'Send notification to counterparty' },
};

const DEFAULT_POSITION = { x: 100, y: 200 };

function getNextPosition(existingNodes: WorkflowNode[]): { x: number; y: number } {
  if (existingNodes.length === 0) {return DEFAULT_POSITION;}
  const maxX = Math.max(...existingNodes.map((n) => n.position.x));
  const avgY =
    existingNodes.reduce((sum, n) => sum + n.position.y, 0) / existingNodes.length;
  return { x: maxX + NODE_GAP_X, y: avgY };
}

function createInitialState(initialGraph?: InitialGraph): WorkflowState {
  const nodesById: Record<string, WorkflowNode> = {};
  const edgesById: Record<string, WorkflowEdge> = {};
  if (initialGraph != null) {
    for (const node of initialGraph.nodes) {
      nodesById[node.id] = node;
    }
    for (const edge of initialGraph.edges) {
      edgesById[edge.id] = edge;
    }
  }
  return {
    meta: { name: initialGraph?.name ?? 'Untitled Workflow', isDirty: false },
    nodesById,
    edgesById,
    pickerState: { open: false, mode: PickerMode.Add },
    configNodeId: null,
  };
}

export function useWorkflowStore(workflowId?: string, initialGraph?: InitialGraph) {
  const [store] = useState(() =>
    createStore<WorkflowStore>()(
      devtools(
        immer((set) => ({
          ...createInitialState(initialGraph),
          actions: {
            setName: (name: string) => {
              set((state) => {
                state.meta.name = name;
                state.meta.isDirty = true;
              });
            },

            onNodesChange: (changes: NodeChange[]) => {
              set((state) => {
                for (const change of changes) {
                  if (change.type === 'position') {
                    const node = state.nodesById[change.id];
                    if (node == null) {continue;}
                    if (change.position != null) {
                      node.position = change.position;
                    }
                    if (change.dragging != null) {
                      node.dragging = change.dragging;
                    }
                  } else if (change.type === 'dimensions') {
                    const node = state.nodesById[change.id];
                    if (node == null) {continue;}
                    if (change.dimensions != null) {
                      node.measured = {
                        width: change.dimensions.width,
                        height: change.dimensions.height,
                      };
                    }
                  } else if (change.type === 'select') {
                    const node = state.nodesById[change.id];
                    if (node == null) {continue;}
                    node.selected = change.selected;
                  } else if (change.type === 'remove') {
                    detachNode(state, change.id);
                  }
                }
              });
            },

            onEdgesChange: (changes: EdgeChange[]) => {
              set((state) => {
                for (const change of changes) {
                  if (change.type === 'select') {
                    const edge = state.edgesById[change.id];
                    if (edge != null) {
                      edge.selected = change.selected;
                    }
                  } else if (change.type === 'remove') {
                    delete state.edgesById[change.id];
                  }
                }
              });
            },

            addTriggerNode: (triggerType: TriggerType, position?: { x: number; y: number }) => {
              const id = uuidv4();
              set((state) => {
                const pos = position ?? getNextPosition(Object.values(state.nodesById));
                state.nodesById[id] = {
                  id,
                  type: 'trigger',
                  position: pos,
                  data: {
                    nodeType: WorkflowNodeType.Trigger,
                    triggerType,
                    ...TRIGGER_LABELS[triggerType],
                    status: NodeStatus.Valid,
                  },
                };
                state.meta.isDirty = true;
              });
              return id;
            },

            addActionNode: (actionType: ActionType, position?: { x: number; y: number }) => {
              const id = uuidv4();
              set((state) => {
                const pos = position ?? getNextPosition(Object.values(state.nodesById));
                state.nodesById[id] = {
                  id,
                  type: 'action',
                  position: pos,
                  data: {
                    nodeType: WorkflowNodeType.Action,
                    actionType,
                    ...ACTION_LABELS[actionType],
                    status: NodeStatus.Valid,
                    config: {},
                  },
                };
                state.meta.isDirty = true;
              });
              return id;
            },

            addRoutingNode: (position?: { x: number; y: number }) => {
              const id = uuidv4();
              set((state) => {
                const pos = position ?? getNextPosition(Object.values(state.nodesById));
                state.nodesById[id] = {
                  id,
                  type: 'routing',
                  position: pos,
                  data: {
                    nodeType: WorkflowNodeType.Routing,
                    label: 'Routing',
                    description: 'Classify and route to the right path',
                    status: NodeStatus.Valid,
                    paths: [
                      { id: uuidv4(), label: 'Path 1', description: 'Default path' },
                    ],
                    inputs: [],
                  },
                };
                state.meta.isDirty = true;
              });
              return id;
            },

            addPaymentLinkNode: (position?: { x: number; y: number }) => {
              const id = uuidv4();
              set((state) => {
                const pos = position ?? getNextPosition(Object.values(state.nodesById));
                state.nodesById[id] = {
                  id,
                  type: 'paymentLink',
                  position: pos,
                  data: {
                    nodeType: WorkflowNodeType.PaymentLink,
                    label: 'Payment link',
                    description: 'Collections feed the staging pool',
                    status: NodeStatus.Valid,
                    currency: 'GHS',
                  },
                };
                state.meta.isDirty = true;
              });
              return id;
            },

            addSupplierPayoutNode: (position?: { x: number; y: number }) => {
              const id = uuidv4();
              set((state) => {
                const pos = position ?? getNextPosition(Object.values(state.nodesById));
                state.nodesById[id] = {
                  id,
                  type: 'supplierPayout',
                  position: pos,
                  data: {
                    nodeType: WorkflowNodeType.SupplierPayout,
                    label: 'Pay supplier',
                    description: 'Send USDC to a supplier wallet',
                    status: NodeStatus.Valid,
                  },
                };
                state.meta.isDirty = true;
              });
              return id;
            },

            setRoutingPolicy: (nodeId: string, policy: PaymentRoutingPolicy) => {
              set((state) => {
                const node = state.nodesById[nodeId];
                if (node == null || node.data.nodeType !== WorkflowNodeType.Routing) {
                  return;
                }
                (node.data as { policy?: PaymentRoutingPolicy }).policy = policy;
                state.meta.isDirty = true;
              });
            },

            addSupplierBranch: (routingNodeId: string) => {
              set((state) => {
                const routingNode = state.nodesById[routingNodeId];
                if (routingNode == null || routingNode.data.nodeType !== WorkflowNodeType.Routing) {
                  return;
                }
                const routingData = routingNode.data as {
                  paths: RoutingPath[];
                };
                const pathId = uuidv4();
                const priority = routingData.paths.length + 1;
                routingData.paths.push({
                  id: pathId,
                  label: 'Supplier',
                  description: `Priority ${priority}`,
                });
                // Stack the new payout below the lowest existing branch target so
                // it never overlaps one (priority-based offset breaks after a delete).
                const branchTargetYs = Object.values(state.edgesById)
                  .filter(
                    (e) =>
                      e.source === routingNodeId &&
                      typeof e.sourceHandle === 'string' &&
                      e.sourceHandle.startsWith(PATH_HANDLE_PREFIX),
                  )
                  .map((e) => state.nodesById[e.target]?.position.y)
                  .filter((y): y is number => y != null);
                const nextY =
                  branchTargetYs.length > 0
                    ? Math.max(...branchTargetYs) + 120
                    : routingNode.position.y;
                // Spawn a connected supplier payout node for the new branch.
                const supplierNodeId = uuidv4();
                state.nodesById[supplierNodeId] = {
                  id: supplierNodeId,
                  type: 'supplierPayout',
                  position: {
                    x: routingNode.position.x + 400,
                    y: nextY,
                  },
                  data: {
                    nodeType: WorkflowNodeType.SupplierPayout,
                    label: 'Pay supplier',
                    description: 'Send USDC to a supplier wallet',
                    status: NodeStatus.Valid,
                  },
                };
                const edgeId = `e-${routingNodeId}-${PATH_HANDLE_PREFIX}${pathId}-${supplierNodeId}`;
                state.edgesById[edgeId] = {
                  id: edgeId,
                  source: routingNodeId,
                  target: supplierNodeId,
                  sourceHandle: `path-${pathId}`,
                  type: 'workflow',
                };
                state.meta.isDirty = true;
              });
            },

            removeNode: (nodeId: string) => {
              set((state) => {
                detachNode(state, nodeId);
                state.meta.isDirty = true;
                if (state.configNodeId === nodeId) {
                  state.configNodeId = null;
                }
              });
            },

            updateNodeData: (nodeId: string, data: Partial<WorkflowNode['data']>) => {
              set((state) => {
                const node = state.nodesById[nodeId];
                if (node == null) {return;}
                node.data = { ...node.data, ...data } as WorkflowNode['data'];
                // A payout's amount/supplier feeds the routing node's ranked list.
                if (node.data.nodeType === WorkflowNodeType.SupplierPayout) {
                  syncRoutingPathFromPayout(state, nodeId);
                }
                state.meta.isDirty = true;
              });
            },

            addEdge: (sourceId: string, targetId: string, sourceHandle?: string) => {
              set((state) => {
                // A payment link feeds exactly one staging pool: drop any existing
                // outgoing edge from this link before wiring it to a new routing node.
                const sourceNode = state.nodesById[sourceId];
                if (sourceNode?.data.nodeType === WorkflowNodeType.PaymentLink) {
                  for (const edge of Object.values(state.edgesById)) {
                    if (edge.source === sourceId) {
                      delete state.edgesById[edge.id];
                    }
                  }
                }
                // Include the source handle in the id so two ranked paths from the
                // same routing node to the same target don't collide on one id.
                const id =
                  sourceHandle != null
                    ? `e-${sourceId}-${sourceHandle}-${targetId}`
                    : `e-${sourceId}-${targetId}`;
                state.edgesById[id] = {
                  id,
                  source: sourceId,
                  target: targetId,
                  sourceHandle,
                  type: 'workflow',
                };
                state.meta.isDirty = true;
              });
            },

            removeEdge: (edgeId: string) => {
              set((state) => {
                delete state.edgesById[edgeId];
                state.meta.isDirty = true;
              });
            },

            openPicker: (pickerState: Omit<PickerState, 'open'>) => {
              set((state) => {
                state.pickerState = { ...pickerState, open: true };
                state.configNodeId = null;
              });
            },

            closePicker: () => {
              set((state) => {
                state.pickerState = { open: false, mode: PickerMode.Add };
              });
            },

            openConfig: (nodeId: string) => {
              set((state) => {
                state.configNodeId = nodeId;
                state.pickerState = { open: false, mode: PickerMode.Add };
              });
            },

            closeConfig: () => {
              set((state) => {
                state.configNodeId = null;
              });
            },

            replaceNode: (
              nodeId: string,
              _catalogItemId: string,
              nodeType: WorkflowNodeType,
              triggerType?: TriggerType,
              actionType?: ActionType,
            ) => {
              set((state) => {
                const node = state.nodesById[nodeId];
                if (node == null) {return;}

                if (nodeType === WorkflowNodeType.Trigger && triggerType != null) {
                  node.type = 'trigger';
                  node.data = {
                    nodeType: WorkflowNodeType.Trigger,
                    triggerType,
                    ...TRIGGER_LABELS[triggerType],
                    status: NodeStatus.Valid,
                  };
                } else if (nodeType === WorkflowNodeType.Action && actionType != null) {
                  node.type = 'action';
                  node.data = {
                    nodeType: WorkflowNodeType.Action,
                    actionType,
                    ...ACTION_LABELS[actionType],
                    status: NodeStatus.Valid,
                    config: {},
                  };
                } else if (nodeType === WorkflowNodeType.Routing) {
                  node.type = 'routing';
                  node.data = {
                    nodeType: WorkflowNodeType.Routing,
                    label: 'Routing',
                    description: 'Classify and route to the right path',
                    status: NodeStatus.Valid,
                    paths: [{ id: uuidv4(), label: 'Path 1', description: 'Default path' }],
                    inputs: [],
                  };
                }
                state.meta.isDirty = true;
              });
            },

            insertNodeOnEdge: (edgeId: string, newNodeId: string) => {
              set((state) => {
                const edge = state.edgesById[edgeId];
                if (edge == null) {return;}

                const newNode = state.nodesById[newNodeId];
                if (newNode == null) {return;}

                const sourceNode = state.nodesById[edge.source];
                const targetNode = state.nodesById[edge.target];
                if (sourceNode == null || targetNode == null) {return;}

                newNode.position = {
                  x: (sourceNode.position.x + targetNode.position.x) / 2,
                  y: (sourceNode.position.y + targetNode.position.y) / 2,
                };

                targetNode.position = {
                  x: targetNode.position.x + NODE_GAP_X / 2,
                  y: targetNode.position.y,
                };

                delete state.edgesById[edgeId];
                const edgeToNew = `e-${edge.source}-${newNodeId}`;
                state.edgesById[edgeToNew] = {
                  id: edgeToNew,
                  source: edge.source,
                  target: newNodeId,
                  sourceHandle: edge.sourceHandle,
                  type: 'workflow',
                };
                const edgeFromNew = `e-${newNodeId}-${edge.target}`;
                state.edgesById[edgeFromNew] = {
                  id: edgeFromNew,
                  source: newNodeId,
                  target: edge.target,
                  type: 'workflow',
                };

                state.meta.isDirty = true;
              });
            },

            addRoutingPath: (nodeId: string, path: RoutingPath) => {
              set((state) => {
                const node = state.nodesById[nodeId];
                if (node == null || node.data.nodeType !== WorkflowNodeType.Routing) {return;}
                (node.data as { paths: RoutingPath[] }).paths.push(path);
                state.meta.isDirty = true;
              });
            },

            removeRoutingPath: (nodeId: string, pathId: string) => {
              set((state) => {
                const node = state.nodesById[nodeId];
                if (node == null || node.data.nodeType !== WorkflowNodeType.Routing) {return;}
                const data = node.data as { paths: RoutingPath[] };
                data.paths = data.paths.filter((p) => p.id !== pathId);
                state.meta.isDirty = true;
              });
            },

            discard: () => {
              // Revert to the seeded graph this builder opened with, not an empty one.
              set(() => createInitialState(initialGraph));
            },
          },
        })),
        { name: 'WorkflowStore' },
      ),
    ),
  );

  const nodes = useStore(
    store,
    useShallow((s) => Object.values(s.nodesById)),
  );
  const edges = useStore(
    store,
    useShallow((s) => Object.values(s.edgesById)),
  );
  const meta = useStore(store, (s) => s.meta);
  const pickerState = useStore(store, (s) => s.pickerState);
  const configNodeId = useStore(store, (s) => s.configNodeId);
  const actions = useStore(store, (s) => s.actions);
  const getNode = (id: string) => store.getState().nodesById[id];

  return { nodes, edges, meta, pickerState, configNodeId, actions, getNode };
}
